import { isNumeric } from './lensChart';

/**
 * The small expression language calculated fields are written in.
 *
 * Deliberately not SQL, and deliberately not JavaScript. It is evaluated in the
 * browser against rows a dataset already returned, so it has to be total: every
 * expression either produces a value or produces null, and none of them can
 * throw, loop or reach anything outside the row it is given.
 *
 * The spelling follows SQL, because the people writing these write SQL: `=` for
 * equality, `<>` for inequality, `and`/`or`/`not`, single-quoted strings, and
 * division that yields null rather than an error when the denominator is zero,
 * which is what SAFE_DIVIDE does.
 */

export type FieldNode =
  | { kind: 'number'; value: number }
  | { kind: 'string'; value: string }
  | { kind: 'null' }
  | { kind: 'boolean'; value: boolean }
  | { kind: 'column'; name: string }
  | { kind: 'unary'; operator: '-' | 'not'; operand: FieldNode }
  | { kind: 'binary'; operator: BinaryOperator; left: FieldNode; right: FieldNode }
  | { kind: 'call'; name: FunctionName; args: Array<FieldNode> };

type BinaryOperator = '+' | '-' | '*' | '/' | '=' | '<>' | '>' | '>=' | '<' | '<=' | 'and' | 'or';

const FUNCTIONS = ['if', 'coalesce', 'abs', 'round'] as const;
export type FunctionName = (typeof FUNCTIONS)[number];

/** Lowest to highest, which is the order the parser descends in. */
const PRECEDENCE: Array<Array<BinaryOperator>> = [
  ['or'],
  ['and'],
  ['=', '<>', '>=', '<=', '>', '<'],
  ['+', '-'],
  ['*', '/'],
];

export class FieldExpressionError extends Error {}

type Token = { type: 'number' | 'string' | 'word' | 'punct'; text: string; at: number };

const WORD = /[A-Za-z_][A-Za-z0-9_]*/;
// Two-character operators first, so '>=' is never read as '>' then '='.
const PUNCT = ['<>', '!=', '==', '>=', '<=', '(', ')', ',', '+', '-', '*', '/', '=', '>', '<'];
const PUNCT_SPELLINGS: Record<string, string> = { '!=': '<>', '==': '=' };

function tokenize(source: string): Array<Token> {
  const tokens: Array<Token> = [];
  let at = 0;

  while (at < source.length) {
    const character = source[at];
    if (/\s/.test(character)) {
      at += 1;
      continue;
    }

    if (character === "'") {
      // Doubled quotes escape a quote, as in SQL.
      let text = '';
      let cursor = at + 1;
      while (cursor < source.length) {
        if (source[cursor] === "'") {
          if (source[cursor + 1] === "'") {
            text += "'";
            cursor += 2;
            continue;
          }
          break;
        }
        text += source[cursor];
        cursor += 1;
      }
      if (cursor >= source.length) {
        throw new FieldExpressionError('A quoted value is missing its closing quote.');
      }
      tokens.push({ type: 'string', text, at });
      at = cursor + 1;
      continue;
    }

    if (/[0-9]/.test(character) || (character === '.' && /[0-9]/.test(source[at + 1] ?? ''))) {
      const match = /^\d*\.?\d+/.exec(source.slice(at));
      if (!match) {
        throw new FieldExpressionError(`“${source.slice(at, at + 8)}” is not a number this understands.`);
      }
      tokens.push({ type: 'number', text: match[0], at });
      at += match[0].length;
      continue;
    }

    const word = WORD.exec(source.slice(at));
    if (word && word.index === 0) {
      tokens.push({ type: 'word', text: word[0], at });
      at += word[0].length;
      continue;
    }

    const punct = PUNCT.find((candidate) => source.startsWith(candidate, at));
    if (punct) {
      tokens.push({ type: 'punct', text: PUNCT_SPELLINGS[punct] ?? punct, at });
      at += punct.length;
      continue;
    }

    throw new FieldExpressionError(`“${character}” is not something this understands.`);
  }

  return tokens;
}

/**
 * Reads an expression into a tree, or says what is wrong with it in a sentence
 * a reader can act on.
 */
export function parseFieldExpression(source: string): FieldNode {
  const tokens = tokenize(source);
  if (!tokens.length) {
    throw new FieldExpressionError('This field has no formula.');
  }

  let position = 0;
  const peek = () => tokens[position];
  const done = () => position >= tokens.length;

  const eat = (text: string): boolean => {
    const token = peek();
    if (token && (token.type === 'punct' || token.type === 'word') && token.text.toLowerCase() === text) {
      position += 1;
      return true;
    }
    return false;
  };

  const expect = (text: string) => {
    if (!eat(text)) {
      throw new FieldExpressionError(`Expected “${text}”${done() ? ' before the end' : ''}.`);
    }
  };

  const parseAt = (level: number): FieldNode => {
    if (level >= PRECEDENCE.length) {
      return parseUnary();
    }
    let left = parseAt(level + 1);
    for (;;) {
      const operator = PRECEDENCE[level].find((candidate) => {
        const token = peek();
        return Boolean(token) && token.text.toLowerCase() === candidate;
      });
      if (!operator) {
        return left;
      }
      position += 1;
      left = { kind: 'binary', operator, left, right: parseAt(level + 1) };
    }
  };

  function parseUnary(): FieldNode {
    if (eat('-')) {
      return { kind: 'unary', operator: '-', operand: parseUnary() };
    }
    if (eat('not')) {
      return { kind: 'unary', operator: 'not', operand: parseUnary() };
    }
    return parsePrimary();
  }

  function parsePrimary(): FieldNode {
    const token = peek();
    if (!token) {
      throw new FieldExpressionError('The formula ends before it is finished.');
    }

    if (eat('(')) {
      const inner = parseAt(0);
      expect(')');
      return inner;
    }

    if (token.type === 'number') {
      position += 1;
      return { kind: 'number', value: Number(token.text) };
    }

    if (token.type === 'string') {
      position += 1;
      return { kind: 'string', value: token.text };
    }

    if (token.type === 'word') {
      const lowered = token.text.toLowerCase();
      if (lowered === 'null') {
        position += 1;
        return { kind: 'null' };
      }
      if (lowered === 'true' || lowered === 'false') {
        position += 1;
        return { kind: 'boolean', value: lowered === 'true' };
      }
      if ((FUNCTIONS as ReadonlyArray<string>).includes(lowered) && tokens[position + 1]?.text === '(') {
        position += 2;
        const args: Array<FieldNode> = [];
        if (!eat(')')) {
          do {
            args.push(parseAt(0));
          } while (eat(','));
          expect(')');
        }
        checkArity(lowered as FunctionName, args.length);
        return { kind: 'call', name: lowered as FunctionName, args };
      }
      position += 1;
      return { kind: 'column', name: token.text };
    }

    throw new FieldExpressionError(`“${token.text}” cannot start a value.`);
  }

  const tree = parseAt(0);
  if (!done()) {
    throw new FieldExpressionError(`“${tokens[position].text}” is left over at the end of the formula.`);
  }
  return tree;
}

function checkArity(name: FunctionName, count: number) {
  if (name === 'if' && count !== 3) {
    throw new FieldExpressionError('if takes three parts: if(<test>, <then>, <otherwise>).');
  }
  if (name === 'coalesce' && count < 2) {
    throw new FieldExpressionError('coalesce takes at least two values.');
  }
  if (name === 'abs' && count !== 1) {
    throw new FieldExpressionError('abs takes one value.');
  }
  if (name === 'round' && (count < 1 || count > 2)) {
    throw new FieldExpressionError('round takes a value and, optionally, how many decimal places.');
  }
}

/** Every column the expression reads, so a caller knows what it depends on. */
export function fieldExpressionColumns(node: FieldNode): Array<string> {
  const found = new Set<string>();
  const walk = (current: FieldNode) => {
    if (current.kind === 'column') {
      found.add(current.name);
      return;
    }
    if (current.kind === 'unary') {
      walk(current.operand);
      return;
    }
    if (current.kind === 'binary') {
      walk(current.left);
      walk(current.right);
      return;
    }
    if (current.kind === 'call') {
      current.args.forEach(walk);
    }
  };
  walk(node);
  return [...found];
}

/**
 * Evaluates against one row. Null is the answer to anything that cannot be
 * computed, including dividing by zero and reading a column the row does not
 * have, so a field can never render an error where a number belongs.
 */
export function evaluateFieldExpression(node: FieldNode, row: Record<string, unknown>): unknown {
  switch (node.kind) {
    case 'number':
      return node.value;
    case 'string':
      return node.value;
    case 'boolean':
      return node.value;
    case 'null':
      return null;
    case 'column':
      // Own properties only. A plain lookup would resolve `constructor` or
      // `toString` off Object.prototype and hand back a function where the
      // dataset has no such column at all.
      return Object.prototype.hasOwnProperty.call(row, node.name) ? (row[node.name] ?? null) : null;
    case 'unary':
      return evaluateUnary(node, row);
    case 'binary':
      return evaluateBinary(node, row);
    case 'call':
      return evaluateCall(node, row);
    default:
      return null;
  }
}

function evaluateUnary(node: FieldNode & { kind: 'unary' }, row: Record<string, unknown>): unknown {
  const operand = evaluateFieldExpression(node.operand, row);
  if (node.operator === 'not') {
    return operand === null ? null : !truthy(operand);
  }
  const number = asNumber(operand);
  return number === null ? null : -number;
}

function evaluateBinary(node: FieldNode & { kind: 'binary' }, row: Record<string, unknown>): unknown {
  // Short-circuit, so `x <> 0 and y / x > 1` never divides by zero.
  if (node.operator === 'and' || node.operator === 'or') {
    const left = evaluateFieldExpression(node.left, row);
    if (node.operator === 'and' && left !== null && !truthy(left)) {
      return false;
    }
    if (node.operator === 'or' && left !== null && truthy(left)) {
      return true;
    }
    const right = evaluateFieldExpression(node.right, row);
    // Three-valued logic, so this matches what the same expression would do in SQL. A definite
    // false decides an `and` and a definite true decides an `or` whichever side it arrives on, so
    // an unknown on the other side cannot pull a decided result back to unknown.
    if (right !== null) {
      if (node.operator === 'and' && !truthy(right)) {
        return false;
      }
      if (node.operator === 'or' && truthy(right)) {
        return true;
      }
    }
    if (left === null || right === null) {
      return null;
    }
    return node.operator === 'and' ? truthy(left) && truthy(right) : truthy(left) || truthy(right);
  }

  const left = evaluateFieldExpression(node.left, row);
  const right = evaluateFieldExpression(node.right, row);

  if (node.operator === '=' || node.operator === '<>') {
    if (left === null || right === null) {
      return null;
    }
    const same = comparable(left) === comparable(right);
    return node.operator === '=' ? same : !same;
  }

  const a = asNumber(left);
  const b = asNumber(right);
  if (a === null || b === null) {
    // Text still orders and compares, which is what a reader means by
    // `channel = 'Direct'` or `hotel_type > 'A'`.
    if (left === null || right === null) {
      return null;
    }
    const text = String(left).localeCompare(String(right));
    switch (node.operator) {
      case '>':
        return text > 0;
      case '>=':
        return text >= 0;
      case '<':
        return text < 0;
      case '<=':
        return text <= 0;
      default:
        return null;
    }
  }

  switch (node.operator) {
    case '+':
      return a + b;
    case '-':
      return a - b;
    case '*':
      return a * b;
    case '/':
      // Null rather than Infinity: a rate with no denominator is not a very
      // large rate, it is a rate there is no answer to.
      return b === 0 ? null : a / b;
    case '>':
      return a > b;
    case '>=':
      return a >= b;
    case '<':
      return a < b;
    case '<=':
      return a <= b;
    default:
      return null;
  }
}

function evaluateCall(node: FieldNode & { kind: 'call' }, row: Record<string, unknown>): unknown {
  if (node.name === 'if') {
    const test = evaluateFieldExpression(node.args[0], row);
    // Null is not true, which is what SQL does with a CASE whose test is null.
    return truthy(test) ? evaluateFieldExpression(node.args[1], row) : evaluateFieldExpression(node.args[2], row);
  }

  if (node.name === 'coalesce') {
    for (const argument of node.args) {
      const value = evaluateFieldExpression(argument, row);
      if (value !== null && value !== undefined && value !== '') {
        return value;
      }
    }
    return null;
  }

  const first = asNumber(evaluateFieldExpression(node.args[0], row));
  if (first === null) {
    return null;
  }
  if (node.name === 'abs') {
    return Math.abs(first);
  }

  const places = node.args[1] ? asNumber(evaluateFieldExpression(node.args[1], row)) : 0;
  const factor = 10 ** Math.min(10, Math.max(0, Math.trunc(places ?? 0)));
  return Math.round(first * factor) / factor;
}

function truthy(value: unknown): boolean {
  if (value === null || value === undefined || value === false || value === '') {
    return false;
  }
  return value !== 0;
}

/** Numbers arrive from BigQuery as strings often enough that a formula has to
 *  read '1200' as a number, but '' and 'Direct' as not numbers at all. */
function asNumber(value: unknown): number | null {
  return isNumeric(value) ? Number(value) : null;
}

/** Equality across the string/number boundary BigQuery straddles. */
function comparable(value: unknown): string {
  return isNumeric(value) ? String(Number(value)) : String(value);
}
